import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './infrastructure.scss';
import { getDepartmentDetails } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';
import { Skeleton } from '../../components/Skeleton';

// Year extraction & sorting helper to ensure latest years appear first
const extractYear = (item) => {
    if (!item) return 0;
    if (typeof item === 'string') {
        const matches = item.match(/\b(19\d{2}|20\d{2})\b/g);
        if (matches) return Math.max(...matches.map(Number));
        return 0;
    }
    const str = `${item.title || ''} ${item.name || ''} ${item.date || ''} ${item.year || ''} ${item.key || ''}`;
    const matches = str.match(/\b(19\d{2}|20\d{2})\b/g);
    if (matches) return Math.max(...matches.map(Number));
    return 0;
};

const sortByLatestYear = (list = []) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
        const yearA = extractYear(a);
        const yearB = extractYear(b);
        if (yearA !== yearB) return yearB - yearA;
        return 0;
    });
};

// Helper Components
const DocumentList = ({ title, items }) => {
    if(!items || items.length === 0) return null;
    const sortedItems = sortByLatestYear(items);
    return (
        <div className="lw-research-card-block" style={{ marginBottom: '1.5rem', width: '100%' }}>
            <div className="lw-card-block-header" style={{ marginBottom: '1.25rem' }}>
                <div className="lw-card-block-header-left">
                    <span className="lw-card-block-tag">Academic Participation</span>
                    <h3 className="lw-card-block-title" style={{ fontSize: '1.35rem' }}>{title}</h3>
                </div>
                <span className="lw-card-block-count">{sortedItems.length} {sortedItems.length === 1 ? 'Record' : 'Records'}</span>
            </div>
            <div className="lw-doc-list-grid" style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                {sortedItems.map((item, idx) => {
                    const itemLink = item.pdf || item.link;
                    const text = item.title || item.name || item.industry;
                    return (
                        <div 
                            key={idx} 
                            className="lw-doc-list-item"
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '14px',
                                padding: '1.1rem 1.25rem',
                                background: '#f8fafc',
                                border: '1px solid #eef2f6',
                                borderRadius: '14px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div 
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '8px',
                                    background: 'rgba(14, 36, 85, 0.08)',
                                    color: '#0e2455',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 800,
                                    flexShrink: 0,
                                    marginTop: '2px',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {String(idx + 1).padStart(2, '0')}
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <p style={{ margin: 0, color: '#1e293b', fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.5, textAlign: 'justify' }}>
                                    {text}
                                </p>
                                {item.date && (
                                    <span style={{ color: '#F6872A', fontSize: '0.78rem', fontWeight: 700 }}>
                                        {item.date}
                                    </span>
                                )}
                            </div>
                            {itemLink && (
                                <a 
                                    href={mediaUrl(itemLink)} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    style={{
                                        flexShrink: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: '#0e2455',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        textDecoration: 'none'
                                    }}
                                >
                                    <span>Doc</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CategorizedList = ({ categories }) => {
    if(!categories) return null;
    return (
        <div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 350px), 1fr))', gap: '1.5rem'}}>
                {Object.entries(categories).map(([key, section]) => {
                    if(!section) return null;
                    const title = section.title || key;
                    const items = Array.isArray(section) ? section : section.items;
                    if(!items) return null;
                    return <DocumentList key={key} title={title} items={items} />;
                })}
            </div>
        </div>
    );
};

const EventTable = ({ data, expandable = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if(!data || !data.items || data.items.length === 0) return null;

    const visibleItems = expandable && !isExpanded ? data.items.slice(0, 3) : data.items;

    return (
        <div className="dept-course-details-section" style={{marginBottom: '2.5rem'}}>
            <div className="premium-header" style={{padding: '0 0 1rem 0', marginBottom: 0}}>
                <h2 style={{fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: 0, letterSpacing: '-0.5px'}}>{data.title || 'Events'}</h2>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '15px', marginTop: '0.5rem'}}>
                {visibleItems.map((item, idx) => (
                    <div key={idx} style={{padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '10px', border: '1px solid #f1f5f9', background: '#ffffff'}}>
                        <h4 style={{margin: 0, color: '#0e2455', fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.4}}>{item.title || item.name}</h4>
                        {item.resourcePerson && <p style={{margin: 0, color: '#F6872A', fontSize: '0.85rem', fontWeight: 600}}>{item.resourcePerson}</p>}
                        {item.date && <p style={{margin: 0, color: '#64748b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px'}}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                            {item.date}
                        </p>}
                        {(item.pdf || item.link) && (
                            <a href={mediaUrl(item.pdf || item.link)} target="_blank" rel="noreferrer" style={{marginTop: 'auto', paddingTop: '10px', fontSize: '0.85rem', color: '#3b82f6', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'color 0.2s ease'}}>
                                View Details
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                            </a>
                        )}
                    </div>
                ))}
            </div>

            {expandable && data.items.length > 3 && (
                <div style={{textAlign: 'left', marginTop: '1.5rem', width: '100%'}}>
                    <button className="premium-btn" onClick={() => setIsExpanded(!isExpanded)}>
                        <span>{isExpanded ? "Show Less" : "Load More"}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`${isExpanded ? 'rotated' : ''}`} style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
                    </button>
                </div>
            )}
        </div>
    );
};

const AccordionWrapper = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div style={{ marginBottom: '2rem', background: '#ffffff', borderRadius: '24px', boxShadow: '0 4px 20px rgba(14, 36, 85, 0.05)', overflow: 'hidden', marginTop: '2rem' }}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                style={{ padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: isOpen ? '#f8fafc' : '#ffffff', transition: 'background 0.3s ease' }}
            >
                <h2 style={{ fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>{title}</h2>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: isOpen ? '#0e2455' : '#f1f5f9', transition: 'all 0.3s ease' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isOpen ? "#ffffff" : "#0e2455"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </div>
            </div>
            <div style={{ display: isOpen ? 'block' : 'none', opacity: isOpen ? 1 : 0, transition: 'opacity 0.3s ease' }}>
                <div style={{ padding: '0 2rem 2.5rem 2rem' }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

const AcademicsInteractiveExplorer = ({ academics }) => {
    const [activeTabId, setActiveTabId] = useState('');
    const [activeSubYear, setActiveSubYear] = useState('');

    const availableTabs = useMemo(() => {
        if (!academics) return [];
        const tabs = [];
        if (academics.syllabus && Object.keys(academics.syllabus).length > 0) {
            tabs.push({ id: 'syllabus', label: 'Scheme & Syllabus', data: academics.syllabus, icon: 'book' });
        }
        const calData = academics.calendar || academics.academicCalendar;
        if (calData && (Array.isArray(calData) ? calData.length > 0 : Object.keys(calData).length > 0)) {
            tabs.push({ id: 'calendar', label: 'Academic Calendar', data: calData, icon: 'calendar' });
        }
        if (academics.courseFiles && (Array.isArray(academics.courseFiles) ? academics.courseFiles.length > 0 : Object.keys(academics.courseFiles).length > 0)) {
            tabs.push({ id: 'courseFiles', label: 'Course Files', data: academics.courseFiles, icon: 'file' });
        }
        if (academics.labManuals && (Array.isArray(academics.labManuals) ? academics.labManuals.length > 0 : Object.keys(academics.labManuals).length > 0)) {
            tabs.push({ id: 'labManuals', label: 'Lab Manuals', data: academics.labManuals, icon: 'code' });
        }
        return tabs;
    }, [academics]);

    useEffect(() => {
        if (!activeTabId && availableTabs.length > 0) {
            setActiveTabId(availableTabs[0].id);
        }
    }, [availableTabs, activeTabId]);

    const activeTabData = availableTabs.find(t => t.id === activeTabId) || availableTabs[0];

    // Determine sub-groups (e.g. academic years or scheme batches)
    const subGroups = useMemo(() => {
        if (!activeTabData || !activeTabData.data) return [];
        const raw = activeTabData.data;
        if (Array.isArray(raw)) {
            return [{ key: 'all', title: 'All Documents', items: sortByLatestYear(raw) }];
        }
        const groups = Object.entries(raw).map(([key, val]) => {
            const items = Array.isArray(val) ? val : (val.items || []);
            const title = val.title || key.replace(/_/g, ' ').toUpperCase();
            return { key, title, items: sortByLatestYear(items) };
        });
        return groups.sort((a, b) => {
            const yearA = Math.max(extractYear(a.title), extractYear(a.key));
            const yearB = Math.max(extractYear(b.title), extractYear(b.key));
            if (yearA !== yearB) return yearB - yearA;
            return 0;
        });
    }, [activeTabData]);

    useEffect(() => {
        if (subGroups.length > 0) {
            setActiveSubYear(subGroups[0].key);
        } else {
            setActiveSubYear('');
        }
    }, [subGroups, activeTabId]);

    if (!academics || availableTabs.length === 0) return null;

    const currentSubGroup = subGroups.find(g => g.key === activeSubYear) || subGroups[0];
    const displayItems = currentSubGroup ? currentSubGroup.items : [];

    return (
        <div className="lw-academics-root">
            {/* Top Navigation Pill Tabs */}
            <div className="lw-academics-nav">
                {availableTabs.map((tab, idx) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`lw-academics-nav-btn ${activeTabId === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTabId(tab.id)}
                    >
                        <span className="lw-nav-btn-idx">{String(idx + 1).padStart(2, '0')}</span>
                        <span className="lw-nav-btn-label">{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Main Explorer Card */}
            <div className="lw-academics-card">
                <div className="lw-academics-header">
                    <div>
                        <span className="lw-academics-subtag">Academic Resources</span>
                        <h3 className="lw-academics-title">{activeTabData?.label}</h3>
                    </div>
                    {subGroups.length > 1 && (
                        <div className="lw-subyear-pill-group">
                            {subGroups.map((g) => (
                                <button
                                    key={g.key}
                                    type="button"
                                    className={`lw-subyear-pill ${activeSubYear === g.key ? 'active' : ''}`}
                                    onClick={() => setActiveSubYear(g.key)}
                                >
                                    {g.title}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* SubGroup Heading Banner */}
                {currentSubGroup && (
                    <div className="lw-subgroup-banner">
                        <div className="lw-subgroup-info">
                            <span className="lw-subgroup-title">{currentSubGroup.title}</span>
                            <span className="lw-subgroup-count">{displayItems.length} {displayItems.length === 1 ? 'Document' : 'Documents'}</span>
                        </div>
                    </div>
                )}

                {/* Lightwind Cards Grid */}
                <div className="lw-academics-grid">
                    {displayItems.map((item, idx) => {
                        const itemLink = item.pdf || item.link;
                        return (
                            <div key={idx} className="lw-academic-doc-card">
                                <div className="lw-doc-card-top">
                                    <div className="lw-doc-badge">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                        <span>PDF</span>
                                    </div>
                                    <span className="lw-doc-index">#{String(idx + 1).padStart(2, '0')}</span>
                                </div>
                                <h4 className="lw-doc-title">{item.title || item.name}</h4>
                                {itemLink ? (
                                    <a
                                        href={mediaUrl(itemLink)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="lw-doc-view-btn"
                                    >
                                        <span>View Document</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                    </a>
                                ) : (
                                    <div className="lw-doc-nodoc">Coming Soon</div>
                                )}
                            </div>
                        );
                    })}
                    {displayItems.length === 0 && (
                        <div className="lw-academics-empty">
                            <p>No documents uploaded for this session yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* =====================================================================
   INFRASTRUCTURE — interactive "Lab Explorer"
   Parses the existing HTML `description` strings into structured fields
   at render time (the source JSON is left untouched).
   ===================================================================== */

// Split an HTML description into spec key/values + free-text intro lines.
const parseInfraDescription = (html = '') => {
    const parts = html
        .split(/<br\s*\/?>/i)
        .map(s => s.replace(/&nbsp;/g, ' ').trim())
        .filter(Boolean);

    const specs = {};
    const intro = [];
    parts.forEach(part => {
        const m = part.match(/^<strong>\s*(.*?)\s*:?\s*<\/strong>\s*:?\s*(.*)$/i);
        if (m && m[2]) {
            specs[m[1].trim().toLowerCase()] = m[2].replace(/<\/?[^>]+>/g, '').trim();
        } else {
            intro.push(part);
        }
    });
    return { specs, intro };
};

const getInitials = (name = '') =>
    name
        .replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase() || 'NA';

const InfraIcon = ({ d, size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);

const InfraLabPanel = ({ item }) => {
    const { specs, intro } = parseInfraDescription(item.description);
    const known = ['batch size', 'equipment', 'technical staff', 'designation', 'qualification', 'working hours'];

    const batch = specs['batch size'];
    const hours = specs['working hours'];
    const equipment = specs['equipment'];
    const staff = specs['technical staff'];
    const designation = specs['designation'];
    const qualification = specs['qualification'];
    const extraSpecs = Object.entries(specs).filter(([k]) => !known.includes(k));

    const equipChips = equipment
        ? equipment.split(/[,;]/).map(s => s.trim()).filter(Boolean)
        : [];

    return (
        <div className="infx-panel" key={item.title}>
            <div className="infx-banner">
                {item.image
                    ? <img src={mediaUrl(item.image)} alt={item.title} />
                    : <img src={`https://picsum.photos/seed/${encodeURIComponent(item.title).replace(/[^a-zA-Z0-9]/g, '')}/800/400`} alt={item.title} />}
                <span className="infx-banner-tag">
                    <InfraIcon d={<><path d="M3 21l18 0" /><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" /><path d="M9 9l1 0" /><path d="M9 13l1 0" /><path d="M9 17l1 0" /><path d="M14 9l1 0" /><path d="M14 13l1 0" /><path d="M14 17l1 0" /></>} size={14} />
                    Laboratory
                </span>
                <h3 className="infx-banner-title">{item.title}</h3>
            </div>

            <div className="infx-panel-body">
                {(batch || hours) && (
                    <div className="infx-stats">
                        {batch && (
                            <div className="infx-stat">
                                <div className="infx-stat-num">{batch}<span className="unit">seats</span></div>
                                <div className="infx-stat-label">Batch Capacity</div>
                            </div>
                        )}
                        {hours && (
                            <div className="infx-stat">
                                <div className="infx-stat-num">{(hours.match(/\d+/) || [hours])[0]}<span className="unit">hrs</span></div>
                                <div className="infx-stat-label">Working Hours</div>
                            </div>
                        )}
                    </div>
                )}

                {intro.length > 0 && (
                    <div className="infx-block">
                        <p style={{ margin: 0, color: '#475569', fontSize: '1rem', lineHeight: 1.7, textAlign: 'justify' }}
                           dangerouslySetInnerHTML={{ __html: intro.join('<br/>') }} />
                    </div>
                )}

                {equipChips.length > 0 && (
                    <div className="infx-block">
                        <h4 className="infx-block-title">
                            <InfraIcon d={<><path d="M3 5a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1z" /><path d="M7 20l10 0" /><path d="M9 16l0 4" /><path d="M15 16l0 4" /></>} />
                            Equipment &amp; Software
                        </h4>
                        <div className="infx-chips">
                            {equipChips.map((chip, i) => <span key={i} className="infx-chip">{chip}</span>)}
                        </div>
                    </div>
                )}

                {extraSpecs.length > 0 && (
                    <div className="infx-block">
                        <h4 className="infx-block-title">
                            <InfraIcon d={<><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /></>} />
                            Specifications
                        </h4>
                        <div className="infx-specs">
                            {extraSpecs.map(([k, v]) => (
                                <div key={k} className="infx-spec-row">
                                    <div className="infx-spec-key" style={{ textTransform: 'capitalize' }}>{k}</div>
                                    <div className="infx-spec-val">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {staff && (
                    <div className="infx-block">
                        <h4 className="infx-block-title">
                            <InfraIcon d={<><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /></>} />
                            Technical In-charge
                        </h4>
                        <div className="infx-staff">
                            <div className="infx-avatar">{getInitials(staff)}</div>
                            <div>
                                <p className="infx-staff-name">{staff}</p>
                                {designation && <p className="infx-staff-role">{designation}</p>}
                                {qualification && <p className="infx-staff-qual">{qualification}</p>}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const InfrastructurePane = ({ data }) => {
    const items = (data && data.items) || [];
    // An item with no parsed key/value specs is treated as an "overview" band.
    const labs = [];
    const overviews = [];
    items.forEach(it => {
        const { specs } = parseInfraDescription(it.description);
        (Object.keys(specs).length > 0 ? labs : overviews).push(it);
    });

    const [activeIdx, setActiveIdx] = useState(0);
    const activeLab = labs[activeIdx] || labs[0];

    return (
        <div className="dept-tab-pane pane-infrastructure infx-root">
            {data.pdf && (
                <div className="infx-topbar">
                    <a href={mediaUrl(data.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW INFRASTRUCTURE DOC</a>
                </div>
            )}

            {/* Feature overview band(s) */}
            {overviews.map((ov, i) => {
                const { intro } = parseInfraDescription(ov.description);
                const lead = intro[0];
                const points = intro.slice(1);
                return (
                    <div className="infx-overview" key={`ov-${i}`}>
                        <div className="infx-overview-body">
                            <span className="infx-overview-kicker">Department Facilities</span>
                            <h2 className="infx-overview-title">{ov.title}</h2>
                            {lead && <p className="infx-overview-lead" dangerouslySetInnerHTML={{ __html: lead }} />}
                            {points.length > 0 && (
                                <ul className="infx-overview-list">
                                    {points.map((pt, idx) => (
                                        <li key={idx}>
                                            <span className="infx-check">
                                                <InfraIcon d={<><path d="M5 12l5 5l10 -10" /></>} size={15} />
                                            </span>
                                            <span dangerouslySetInnerHTML={{ __html: pt }} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="infx-overview-media">
                            {ov.image
                                ? <img src={mediaUrl(ov.image)} alt={ov.title} />
                                : <img src={`https://picsum.photos/seed/${encodeURIComponent(ov.title).replace(/[^a-zA-Z0-9]/g, '')}/800/400`} alt={ov.title} />}
                        </div>
                    </div>
                );
            })}

            {/* Interactive lab explorer */}
            {labs.length > 0 && (
                <>
                    <div className="infx-section-head">
                        <span className="infx-eyebrow">Explore the labs</span>
                        <h2 className="infx-section-title">Laboratory Infrastructure</h2>
                        <p className="infx-section-sub">
                            Select a facility to view its capacity, equipment, software stack and technical in-charge.
                        </p>
                    </div>

                    <div className="infx-explorer">
                        <div className="infx-rail" role="tablist" aria-label="Laboratories">
                            <div className="infx-rail-head">{labs.length} Facilities</div>
                            {labs.map((lab, i) => {
                                const { specs } = parseInfraDescription(lab.description);
                                return (
                                    <button
                                        key={i}
                                        role="tab"
                                        aria-selected={activeIdx === i}
                                        className={`infx-rail-item ${activeIdx === i ? 'is-active' : ''}`}
                                        onClick={() => setActiveIdx(i)}
                                    >
                                        <span className="infx-rail-num">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="infx-rail-text">
                                            <span className="infx-rail-name">{lab.title}</span>
                                            {specs['batch size'] && (
                                                <span className="infx-rail-meta">Batch · {specs['batch size']} seats</span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {activeLab && <InfraLabPanel item={activeLab} />}
                    </div>
                </>
            )}
        </div>
    );
};

// Derive a human-friendly label for a faculty link. Uploaded PDFs are stored
// as <timestamp>-<filename>, so a leading number run is stripped.
const deriveLinkLabel = (content) => {
  try {
    const name = decodeURIComponent(String(content).split(/[?#]/)[0].split("/").pop() || "");
    const base = name
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/^\d+[-_]+\s*/, "")
      .replace(/[-_]+/g, " ")
      .trim();
    if (!base) return "";
    return base
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  } catch {
    return "";
  }
};

const FacultyCard = ({ member, idx }) => {
    // A single optional PDF (uploaded in the admin panel). The link icon opens
    // the document directly in a new tab — no intermediate menu.
    const link = (member.details || []).find((l) => l && l.content && String(l.content).trim());
    const linkLabel = link
        ? (link.title && String(link.title).trim()) || deriveLinkLabel(link.content) || 'Document'
        : '';

    return (
        <div className="faculty-card premium-card-anim" style={{animationDelay: `${idx * 0.1}s`}}>
            {link && (
                <a
                    href={mediaUrl(link.content)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="faculty-card-link"
                    aria-label={`Open ${linkLabel} of ${member.name}`}
                    title={linkLabel}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                </a>
            )}

            <div className="faculty-card-media" style={{ width: 'calc(100% - 2rem)', height: '260px', borderRadius: '24px', margin: '1rem auto 0', overflow: 'hidden', flexShrink: 0 }}>
                <img
                    src={mediaUrl(member.image || '/images/departments/placeholder-faculty.png')}
                    alt={member.name}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
                />
            </div>

            <div className="faculty-card-body" style={{ gap: '0.2rem', padding: '0.8rem 1.25rem 1.25rem' }}>
                <h4 className="faculty-card-name" style={{ margin: 0 }}>{member.name}</h4>
                {member.designation && <p className="faculty-card-role" style={{ margin: 0 }}>{member.designation}</p>}
                {member.qualification && <p className="faculty-card-qualification" style={{ color: '#9ca3af', fontSize: '0.75rem', fontWeight: 'bold', margin: 0 }}>{member.qualification}</p>}
                {member.bio && (
                    <p 
                        className="faculty-card-bio" 
                        style={{ 
                            margin: '0.3rem 0 0', 
                            textAlign: 'justify', 
                            display: '-webkit-box', 
                            WebkitLineClamp: 3, 
                            lineClamp: 3, 
                            WebkitBoxOrient: 'vertical', 
                            overflow: 'hidden',
                            fontSize: '0.82rem',
                            lineHeight: 1.5,
                            color: '#475569'
                        }}
                    >
                        {member.bio}
                    </p>
                )}
            </div>
        </div>
    );
};

const getCategoryInfo = (id) => {
    const postGrad = ['department-of-mba', 'department-of-mca', 'm-tech-structural-engineering'];
    const basicSciences = ['department-of-mathematics', 'department-of-physics', 'department-of-chemistry', 'social-science-and-foreign-languages'];
    
    if (postGrad.includes(id)) return { name: 'PostGraduation', param: 'pg' };
    if (basicSciences.includes(id)) return { name: 'Basic Sciences', param: 'science' };
    return { name: 'Undergraduation', param: 'ug' };
};

const DepartmentDetails = () => {
    const { id, tab } = useParams();
    const navigate = useNavigate();
    const activeTab = tab || 'about';
    const [department, setDepartment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSyllabusKey, setActiveSyllabusKey] = useState(null);
    const [activeOutcomeTab, setActiveOutcomeTab] = useState('peos');
    const [facultyPage, setFacultyPage] = useState(1);
    const facultyGridRef = useRef(null);

    useEffect(() => {
        let mounted = true;

        // Content is loaded exclusively from the backend (DB-only).
        setLoading(true);
        setDepartment(null);
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);

        (async () => {
            try {
                const res = await getDepartmentDetails(id);
                const remote = res?.data?.data; // doc.data holds the raw content
                if (mounted && remote && Object.keys(remote).length) {
                    setDepartment(remote);
                    if (remote?.academics?.syllabus && !Array.isArray(remote.academics.syllabus)) {
                        setActiveSyllabusKey(Object.keys(remote.academics.syllabus)[0]);
                    }
                }
            } catch (e) {
                // leave department null -> "not found" state
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [id, tab]);

    if (loading) {
        return (
            <>
                <Header />
                <div className="departments-page-root dept-details-root" style={{ minHeight: '80vh' }}>
                    {/* Breadcrumb Skeleton */}
                    <nav className="dept-breadcrumbs" style={{ display: 'flex', gap: '8px', padding: '1rem 0' }}>
                        <Skeleton style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                        <Skeleton style={{ width: '100px', height: '24px', borderRadius: '4px' }} />
                        <Skeleton style={{ width: '150px', height: '24px', borderRadius: '4px' }} />
                    </nav>

                    {/* Hero Banner Skeleton */}
                    <div className="dept-hero-wrap" style={{ padding: '0 1rem', maxWidth: '1200px', margin: '2rem auto 2.5rem auto' }}>
                        <div className="dept-hero-banner" style={{ background: '#f8fafc', borderRadius: '24px', padding: '3.5rem 3rem', height: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Skeleton style={{ width: '150px', height: '24px', borderRadius: '50px', marginBottom: '1.5rem', backgroundColor: '#e2e8f0' }} />
                            <Skeleton style={{ width: '60%', height: '48px', marginBottom: '1rem', backgroundColor: '#e2e8f0' }} />
                            <Skeleton style={{ width: '40%', height: '24px', backgroundColor: '#e2e8f0' }} />
                        </div>
                    </div>

                    {/* Tabs Skeleton */}
                    <div className="dept-tabs-desktop-container">
                        <div className="dept-tabs-desktop-flex">
                            <div className="dept-tabs-pill-box" style={{ display: 'flex', gap: '10px' }}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Skeleton key={i} style={{ width: '120px', height: '40px', borderRadius: '20px' }} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Content Skeleton */}
                    <div style={{ maxWidth: '1200px', margin: '3rem auto', padding: '0 1rem' }}>
                        <Skeleton style={{ width: '30%', height: '32px', marginBottom: '2rem' }} />
                        <Skeleton style={{ width: '100%', height: '20px', marginBottom: '1rem' }} />
                        <Skeleton style={{ width: '100%', height: '20px', marginBottom: '1rem' }} />
                        <Skeleton style={{ width: '80%', height: '20px', marginBottom: '3rem' }} />
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                            <Skeleton style={{ width: '100%', height: '250px', borderRadius: '16px' }} />
                            <Skeleton style={{ width: '100%', height: '250px', borderRadius: '16px' }} />
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!department) {
        return (
            <>
                <Header />
                <div className="dept-details-root" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h2>Department Not Found</h2>
                        <p>The requested academic department does not exist.</p>
                        <Link to="/departments" style={{ color: '#F6872A', fontWeight: 600 }}>← Back to Departments</Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // ALL POSSBILE TABS
    const ALL_TABS = [
        { id: 'about', label: 'About Department', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-info-circle"><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path><path d="M12 9h.01"></path><path d="M11 12h1v4h1"></path></svg>
        )},
        { id: 'hod', label: 'Head of the Department', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-message-reply"><path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12"></path><path d="M11 8l-3 3l3 3"></path><path d="M16 11h-8"></path></svg>
        )},
        { id: 'faculty', label: 'Faculty', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-users"><path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path><path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path></svg>
        )},
        { id: 'academics', label: 'Academics', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-books"><path d="M5 5a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1l0 -14"></path><path d="M9 5a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1l0 -14"></path><path d="M5 8h4"></path><path d="M9 16h4"></path></svg>
        )},
        { id: 'industry', label: 'Industry Interactions', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-building-skyscraper"><path d="M3 21l18 0"></path><path d="M5 21v-14l8 -4v18"></path><path d="M19 21v-10l-6 -4"></path></svg>
        )},
        { id: 'research', label: 'Research and Consultancy', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-search"><path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path><path d="M21 21l-6 -6"></path></svg>
        )},
        { id: 'infrastructure', label: 'Infrastructure', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-building"><path d="M3 21l18 0"></path><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16"></path></svg>
        )},
        { id: 'placements', label: 'Training and Placements', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-briefcase"><path d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -9"></path><path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2"></path></svg>
        )},
        { id: 'reports', label: 'Reports', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-file-report"><path d="M13 17a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path><path d="M12 3v4a1 1 0 0 0 1 1h4"></path><path d="M11.5 21h-6.5a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v2m0 3v4"></path></svg>
        )},
        { id: 'alumni', label: 'Alumni', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-school"><path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"></path><path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"></path></svg>
        )}
    ];

    // Conditionally map available tabs depending on the department data
    const navTabs = ALL_TABS.filter(t => {
        if(t.id === 'about') return !!department.about;
        return !!department[t.id];
    });

    const handleTabChange = (tabId) => {
        if (tabId === 'about') {
            navigate(`/department/${id}`);
        } else {
            navigate(`/department/${id}/${tabId}`);
        }
    };

    const formatTitle = (deptId) => {
        const manualMap = {
            'computer-science-and-engineering-ai-and-ml': 'CSE (AI & ML)',
            'electronics-and-communication-engineering': 'Electronics & Communication Engineering',
            'information-science-and-engineering': 'Information Science & Engineering',
            'cse-data-science': 'CSE (Data Science)',
            'civil-engineering': 'Civil Engineering',
            'cse-iot-and-cyber-security-including-blockchain-technologies': 'CSE (IoT & Cyber Security)',
            'bachelor-in-computer-applications': 'Bachelor in Computer Applications',
            'computer-science-and-engineering': 'Computer Science & Engineering',
            'department-of-mba': 'Master of Business Administration',
            'department-of-mca': 'Master of Computer Applications',
            'mechanical-engineering': 'Mechanical Engineering',
            'm-tech-structural-engineering': 'M.Tech Structural Engineering',
            'department-of-mathematics': 'Department Of Mathematics',
            'department-of-physics': 'Department Of Physics',
            'department-of-chemistry': 'Department Of Chemistry',
            'social-science-and-foreign-languages': 'Social Science & Foreign Languages'
        };
        return manualMap[deptId] || deptId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const dTitle = (department && department.bannerTitle) ? department.bannerTitle : formatTitle(id);

    return (
        <>
            <Header />
            <div className="departments-page-root dept-details-root">
                <nav className="dept-breadcrumbs">
                    <Link to="/" className="breadcrumb-home-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-home">
                            <path d="M5 12l-2 0l9 -9l9 9l-2 0"></path>
                            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7"></path>
                            <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6"></path>
                        </svg>
                    </Link>
                    <div className="breadcrumb-item-container">
                        <span className="breadcrumb-separator">/</span>
                        <Link to="/departments" className="breadcrumb-nav-link">Departments</Link>
                    </div>
                    <div className="breadcrumb-item-container">
                        <span className="breadcrumb-separator">/</span>
                        <Link to={`/departments?tab=${getCategoryInfo(id).param}`} className="breadcrumb-nav-link">{getCategoryInfo(id).name}</Link>
                    </div>
                    <div className="breadcrumb-item-container">
                        <span className="breadcrumb-separator">/</span>
                        {activeTab !== 'about' ? (
                            <Link to={`/department/${id}`} className="breadcrumb-nav-link">{dTitle}</Link>
                        ) : (
                            <span className="breadcrumb-current">{dTitle}</span>
                        )}
                    </div>
                    {activeTab !== 'about' && (
                        <div className="breadcrumb-item-container">
                            <span className="breadcrumb-separator">/</span>
                            <span className="breadcrumb-current">
                                {ALL_TABS.find(t => t.id === activeTab)?.label || 'Details'}
                            </span>
                        </div>
                    )}
                </nav>

                <div className="dept-hero-wrap" style={{ padding: '0 1rem', maxWidth: '1200px', margin: '2rem auto 2.5rem auto' }}>
                    <div className="dept-hero-banner" style={{ position: 'relative', overflow: 'hidden', background: '#0e2455', borderRadius: '24px', padding: '3.5rem 3rem', color: '#ffffff', boxShadow: '0 10px 30px rgba(14, 36, 85, 0.15)' }}>
                        {/* Background Shapes */}
                        <svg style={{ position: 'absolute', right: '-50px', top: '-50px', width: '300px', height: '300px', opacity: '0.05' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#F6872A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path><path d="M2 12h20"></path></svg>
                        
                        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px' }}>
                            <div style={{ display: 'inline-block', padding: '0.4rem 1rem', background: 'rgba(246, 135, 42, 0.15)', border: '1px solid rgba(246, 135, 42, 0.3)', borderRadius: '50px', color: '#F6872A', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '1.5rem' }}>
                                Academic Department
                            </div>
                            <h1 className="dept-hero-title" style={{ fontSize: '3rem', fontWeight: 800, margin: '0 0 1rem 0', lineHeight: 1.1, letterSpacing: '-1px', color: '#ffffff' }}>
                                {dTitle}
                            </h1>
                            <p style={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.8)', margin: 0, fontWeight: 500, lineHeight: 1.6 }}>
                                Exploring excellence in education, research, and innovation.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="dept-tabs-desktop-container">
                    <div className="dept-tabs-desktop-flex">
                        <div className="dept-tabs-pill-box">
                            {navTabs.map(t => (
                                <button
                                    key={t.id}
                                    className={`dept-subpage-tab-btn ${activeTab === t.id ? 'active-tab' : ''}`}
                                    onClick={() => handleTabChange(t.id)}
                                >
                                    {t.icon}
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="dept-tabs-mobile-container">
                    <button 
                        className="dept-mobile-trigger-btn"
                        onClick={() => setIsMobileMenuOpen(true)}
                    >
                        <div className="dept-mobile-btn-left">
                            <div className="dept-mobile-search-circle">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path><path d="M21 21l-6 -6"></path></svg>
                            </div>
                            <div className="dept-mobile-btn-text">
                                <p className="label-sub">Currently Viewing</p>
                                <p className="label-main">{navTabs.find(t => t.id === activeTab)?.label || 'About Department'}</p>
                            </div>
                        </div>
                        <div className="dept-mobile-arrow-circle">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </button>

                    {isMobileMenuOpen && (
                        <div className="dept-mobile-overlay-drawer" style={{zIndex: 1000}}>
                            <div className="dept-mobile-drawer-body">
                                <button 
                                    className="dept-mobile-drawer-close"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                </button>
                                <div className="dept-mobile-drawer-header">
                                    <p className="label-sub">Explore Department</p>
                                    <h2>Quick <br /><span className="text-orange">Navigation</span></h2>
                                </div>
                                <div className="dept-mobile-drawer-grid">
                                    {navTabs.map(t => (
                                        <button
                                            key={t.id}
                                            className="dept-mobile-grid-btn"
                                            onClick={() => {
                                                handleTabChange(t.id);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <div className="dept-mobile-grid-btn-icon">
                                                {t.icon}
                                            </div>
                                            <span className="dept-mobile-grid-btn-text">{t.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="dept-tab-content-container">
                    
                    {/* 1. ABOUT DEPARTMENT TAB */}
                    {activeTab === 'about' && department.about && (
                        <div className="dept-tab-pane pane-about" style={{maxWidth: '1200px', margin: '0 auto', padding: '1.5rem 1rem'}}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
                                
                                {/* 1. About Overview (Flat Card) */}
                                {department.about.about && (
                                    <div className="dept-section-card" style={{ padding: '2.5rem 2rem', background: '#ffffff', borderRadius: '24px', boxShadow: '0 4px 20px rgba(14, 36, 85, 0.05)', gridColumn: '1 / -1' }}>
                                        <h2 style={{ fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: '0 0 1rem 0', letterSpacing: '-0.5px' }}>Department Overview</h2>
                                        <div className="dept-overview-text" style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.6, textAlign: 'justify' }}>
                                            {department.about.about.slice(0, isExpanded ? undefined : 1).map((para, pIdx) => (
                                                <p key={pIdx} style={{marginBottom: '0.8rem', textAlign: 'justify'}} dangerouslySetInnerHTML={{__html: para}}></p>
                                            ))}
                                        </div>
                                        {department.about.about.length > 1 && (
                                            <button className="premium-btn" onClick={() => setIsExpanded(!isExpanded)}>
                                                <span>{isExpanded ? "Show Less" : "Read More"}</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" className={`${isExpanded ? 'rotated' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* 2. Vision and Mission */}
                                <div className="dept-vm-card" style={{ background: '#0e2455', color: '#ffffff', padding: '2rem', borderRadius: '24px', gridColumn: '1 / -1', marginTop: '1.5rem', boxShadow: '0 10px 30px rgba(14, 36, 85, 0.15)', position: 'relative', overflow: 'hidden' }}>
                                    {/* Decorative watermark */}
                                    <svg style={{ position: 'absolute', right: '-50px', bottom: '-50px', width: '300px', height: '300px', opacity: '0.05' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#F6872A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>
                                    
                                    {department.about.vision && (
                                        <div style={{ marginBottom: '1.5rem', position: 'relative', zIndex: 1, maxWidth: '900px' }}>
                                            <h2 style={{ fontSize: '1.8rem', fontFamily: '"Poppins", sans-serif', fontWeight: 700, margin: '0 0 0.5rem 0', letterSpacing: '-0.5px', color: '#ffffff' }}>Our Vision</h2>
                                            <p className="dept-vm-text" style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.95)', lineHeight: 1.6, margin: 0, fontWeight: 500 }}>{department.about.vision}</p>
                                        </div>
                                    )}

                                    {department.about.mission && (
                                        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px' }}>
                                            <h2 style={{ fontSize: '1.8rem', fontFamily: '"Poppins", sans-serif', fontWeight: 600, margin: '0 0 1rem 0', letterSpacing: '-0.5px', color: '#ffffff' }}>Our Mission</h2>
                                            <ul className="dept-vm-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                                {department.about.mission.map((line, idx) => (
                                                    <li className="dept-vm-li" key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, fontWeight: 400 }}>
                                                        <svg style={{ flexShrink: 0, marginTop: '4px' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F6872A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                                        <span>{line}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>


                                {(department.about.peos || department.about.pos || department.about.psos) && (
                                    <div className="dept-section-card dept-po-card" style={{ padding: '2.5rem 2rem', background: '#ffffff', borderRadius: '24px', boxShadow: '0 4px 20px rgba(14, 36, 85, 0.05)', marginTop: '2rem' }}>
                                        <div className="dept-po-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                                            <h2 style={{ fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>Programme Outcomes</h2>
                                            <div className="dept-po-tabs" style={{ display: 'flex', gap: '0.5rem', background: '#f8fafc', padding: '0.35rem', borderRadius: '50px' }}>
                                                {department.about.peos && (
                                                    <button style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 700, borderRadius: '50px', border: 'none', background: activeOutcomeTab === 'peos' ? '#F6872A' : 'transparent', color: activeOutcomeTab === 'peos' ? '#ffffff' : '#64748b', boxShadow: activeOutcomeTab === 'peos' ? '0 4px 10px rgba(246, 135, 42, 0.2)' : 'none', cursor: 'pointer', transition: 'all 0.3s ease' }} onClick={() => setActiveOutcomeTab('peos')}>PEOs</button>
                                                )}
                                                {department.about.pos && (
                                                    <button style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 700, borderRadius: '50px', border: 'none', background: activeOutcomeTab === 'pos' ? '#F6872A' : 'transparent', color: activeOutcomeTab === 'pos' ? '#ffffff' : '#64748b', boxShadow: activeOutcomeTab === 'pos' ? '0 4px 10px rgba(246, 135, 42, 0.2)' : 'none', cursor: 'pointer', transition: 'all 0.3s ease' }} onClick={() => setActiveOutcomeTab('pos')}>POs</button>
                                                )}
                                                {department.about.psos && (
                                                    <button style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 700, borderRadius: '50px', border: 'none', background: activeOutcomeTab === 'psos' ? '#F6872A' : 'transparent', color: activeOutcomeTab === 'psos' ? '#ffffff' : '#64748b', boxShadow: activeOutcomeTab === 'psos' ? '0 4px 10px rgba(246, 135, 42, 0.2)' : 'none', cursor: 'pointer', transition: 'all 0.3s ease' }} onClick={() => setActiveOutcomeTab('psos')}>PSOs</button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="dept-po-content" style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                                            {activeOutcomeTab === 'peos' && department.about.peos && (
                                                <div className="animate-fade-in">
                                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
                                                        {department.about.peos.map((line, idx) => (
                                                            <li key={idx} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                                <div style={{ flexShrink: 0, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0e2455', fontWeight: 700, fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px' }}>PEO {idx + 1}</div>
                                                                <p style={{ margin: 0 }}>{line.replace(/^PEO\s*\d+\s*[:.)-]?\s*/i, '')}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {activeOutcomeTab === 'pos' && department.about.pos && (
                                                <div className="animate-fade-in">
                                                    <div className="dept-po-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                                                        {department.about.pos.map((line, idx) => {
                                                            const match = line.match(/^(PO\d+:\s*[^:]+:)(.*)$/);
                                                            let boldPart = ''; let restPart = line;
                                                            if(match) { boldPart = match[1]; restPart = match[2]; }
                                                            else { const match2 = line.match(/^(PO\d+:)(.*)$/); if(match2) { boldPart = match2[1]; restPart = match2[2]; } }
                                                            // Strip any embedded "POn:" prefix — the numbered badge is the single source of the number.
                                                            boldPart = boldPart.replace(/^PO\s*\d+\s*[:.)-]?\s*/i, '');
                                                            return (
                                                                <div key={idx} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                                                    <div style={{ flexShrink: 0, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0e2455', fontWeight: 700, fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px', marginTop: '2px' }}>PO {idx + 1}</div>
                                                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569', lineHeight: 1.6 }}>{boldPart && <strong style={{ color: '#0e2455', display: 'block', marginBottom: '0.2rem' }}>{boldPart}</strong>}{restPart}</p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {activeOutcomeTab === 'psos' && department.about.psos && (
                                                <div className="animate-fade-in">
                                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
                                                        {department.about.psos.map((line, idx) => (
                                                            <li key={idx} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                                <div style={{ flexShrink: 0, border: '1px solid #F6872A', color: '#F6872A', fontWeight: 700, fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px' }}>PSO {idx + 1}</div>
                                                                <p style={{ margin: 0 }}>{line.replace(/^PSO\s*\d+\s*[:.)-]?\s*/i, '')}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Academic Boards — Board of Studies (BOS), Department Advisory
                                    Board (DAB) & Programme Assessment Committee (PAC). Rendered from
                                    the same card/slider markup; each board shows only when present. */}
                                {department.about.academicBoards && [
                                    { key: 'bos', title: 'Board of Studies (BOS)' },
                                    { key: 'dab', title: 'Department Advisory Board (DAB)' },
                                    { key: 'pac', title: 'Programme Assessment Committee (PAC)' }
                                ].map((board) => {
                                    const members = department.about.academicBoards[board.key];
                                    if (!members || members.length === 0) return null;

                                    return (
                                    <div key={board.key} className="dept-section-card dept-bos-card" style={{ padding: '2.5rem 2rem', background: '#ffffff', borderRadius: '24px', boxShadow: '0 4px 20px rgba(14, 36, 85, 0.05)', marginTop: '2rem' }}>
                                        <h2 style={{ fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: '0 0 1.5rem 0', letterSpacing: '-0.5px' }}>{board.title}</h2>

                                        <div className="dept-bos-slider" style={{ padding: '0 10px 2rem' }}>
                                            <Slider
                                                dots={true}
                                                arrows={true}
                                                swipe={true}
                                                touchMove={true}
                                                draggable={true}
                                                infinite={false}
                                                speed={500}
                                                slidesToShow={3}
                                                slidesToScroll={1}
                                                responsive={[
                                                    { breakpoint: 1024, settings: { slidesToShow: 2, infinite: false } },
                                                    { breakpoint: 600, settings: { slidesToShow: 1, infinite: false } }
                                                ]}
                                            >
                                                {members.map((member, idx) => {
                                                    return (
                                                        <div key={idx} style={{ padding: '10px' }}>
                                                            <div className="dept-bos-member-card" style={{ margin: '0 10px', padding: '1.5rem', borderRadius: '16px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                                                                <div className="dept-bos-member-head" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                                                                    <strong style={{ color: '#0e2455', fontSize: '1.05rem', lineHeight: 1.3 }}>{member.name}</strong>
                                                                    <span style={{ color: '#F6872A', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{member.designation}</span>
                                                                </div>
                                                                <span style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5 }}>{member.category}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </Slider>
                                        </div>
                                    </div>
                                    );
                                })}

                            {department.about.details && department.about.details.length > 0 && (
                                <div className="dept-section-card dept-cd-card" style={{ padding: '2.5rem 2rem', background: '#ffffff', borderRadius: '24px', boxShadow: '0 4px 20px rgba(14, 36, 85, 0.05)', marginTop: '2rem' }}>
                                    <h2 style={{ fontSize: '1.6rem', color: '#0e2455', fontWeight: 800, margin: '0 0 1.5rem 0', letterSpacing: '-0.5px' }}>Course Details</h2>
                                    
                                    <div className="dept-cd-table-wrap" style={{ overflowX: 'auto' }}>
                                        <table className="dept-cd-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                            <tbody>
                                                {department.about.details.map((row, idx) => (
                                                    <tr key={idx} style={{ borderBottom: idx === department.about.details.length - 1 ? 'none' : '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '1.25rem 1.5rem', width: '35%', fontWeight: 700, color: '#ffffff', fontSize: '0.95rem', verticalAlign: 'top', background: '#0e2455', borderBottom: idx === department.about.details.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.1)', borderTopLeftRadius: idx === 0 ? '12px' : '0', borderBottomLeftRadius: idx === department.about.details.length - 1 ? '12px' : '0' }}>
                                                            {row[0]}
                                                        </td>
                                                        <td style={{ padding: '1.25rem 1.5rem', fontWeight: 500, color: '#475569', fontSize: '0.95rem', verticalAlign: 'top', borderTopRightRadius: idx === 0 ? '12px' : '0', borderBottomRightRadius: idx === department.about.details.length - 1 ? '12px' : '0' }} dangerouslySetInnerHTML={{__html: row[1]}}></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. HEAD OF DEPARTMENT TAB */}
                    {activeTab === 'hod' && department.hod && (
                        <div className="dept-tab-pane pane-hod">
                            <div className="premium-academic-layout" style={{padding: 0, maxWidth: '100%', width: '100%'}}>
                                <div className="premium-card hod-unified-card" style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5rem', padding: '2rem 1rem'}}>
                                    {/* HOD PROFILE CARD (Left Side) */}
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flex: '1 1 250px'}}>
                                        <div style={{width: '220px', height: '220px', borderRadius: '50%', overflow: 'hidden', border: '5px solid #f1f5f9', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', marginBottom: '1.5rem'}}>
                                            <img src={mediaUrl(department.hod.hodImage)} alt={department.hod.hodName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                        </div>
                                        <h2 style={{fontSize: '1.8rem', fontWeight: 800, color: '#0e2455', margin: '0 0 0.5rem 0'}}>{department.hod.hodName}</h2>
                                        <p style={{fontSize: '1.2rem', color: '#F6872A', fontWeight: 600, margin: '0 0 1rem 0'}}>{department.hod.hodDesignation}</p>
                                        {department.hod.hodQualification && (
                                            <span style={{background: '#f1f5f9', color: '#0e2455', padding: '8px 20px', borderRadius: '30px', fontSize: '0.95rem', fontWeight: 700}}>
                                                {department.hod.hodQualification}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* HOD MESSAGE (Right Side) */}
                                    <div style={{flex: '3 1 400px', position: 'relative', padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '24px', border: '1px solid #f1f5f9'}}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="currentColor" style={{position: 'absolute', top: '1rem', left: '1rem', color: 'rgba(14, 36, 85, 0.05)'}}>
                                            <path d="M10.99 4.75c-2.46.23-5.26 1.77-6.52 4.13-1.02 1.9-.92 4.38.1 6.3 1.25 2.37 4.14 3.73 6.64 2.87 2.1-.73 3.32-3.13 2.5-5.32-.47-1.25-1.55-2.22-2.8-2.61a5.61 5.61 0 0 0-3.32.07c.07-.63.26-1.21.6-1.74a4.13 4.13 0 0 1 3.52-1.92c.3-.02.58-.29.58-.6v-1.12c0-.04-.01-.08-.02-.12a.66.66 0 0 0-.28-.31Z"></path>
                                            <path d="M22.99 4.75c-2.46.23-5.26 1.77-6.52 4.13-1.02 1.9-.92 4.38.1 6.3 1.25 2.37 4.14 3.73 6.64 2.87 2.1-.73 3.32-3.13 2.5-5.32-.47-1.25-1.55-2.22-2.8-2.61a5.61 5.61 0 0 0-3.32.07c.07-.63.26-1.21.6-1.74a4.13 4.13 0 0 1 3.52-1.92c.3-.02.58-.29.58-.6v-1.12c0-.04-.01-.08-.02-.12a.66.66 0 0 0-.28-.31Z"></path>
                                        </svg>
                                        <h2 style={{fontSize: '1.6rem', margin: '0 0 1rem 0', color: '#0e2455', fontWeight: 800, position: 'relative', zIndex: 1}}>HOD's Message</h2>
                                        <p style={{fontStyle: 'italic', fontSize: '1.15rem', color: '#334155', position: 'relative', zIndex: 1, lineHeight: 1.8, margin: 0, textAlign: 'justify'}}>
                                            "{department.hod.hodMessage}"
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 3. FACULTY TAB */}
                    {activeTab === 'faculty' && department.faculty && department.faculty.items && (
                        <div className="dept-tab-pane pane-faculty" ref={facultyGridRef}>
                            <div className="premium-academic-layout" style={{padding: 0}}>
                                <div className="faculty-card-grid">
                                    {[...(department.faculty.items || [])]
                                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                                        .slice((facultyPage - 1) * 9, facultyPage * 9)
                                        .map((fMember, idx) => (
                                        <FacultyCard key={idx} member={fMember} idx={idx} />
                                    ))}
                                </div>
                                {department.faculty.items && department.faculty.items.length > 9 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '3rem', padding: '1.5rem 0', borderTop: '1px solid #e2e8f0' }}>
                                        <button 
                                            onClick={() => {
                                                setFacultyPage(p => Math.max(1, p - 1));
                                                window.scrollTo({ top: facultyGridRef.current?.offsetTop - 100, behavior: 'smooth' });
                                            }}
                                            disabled={facultyPage === 1}
                                            className="faculty-pagination-btn"
                                        >
                                            Previous Page
                                        </button>
                                        
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {Array.from({ length: Math.ceil(department.faculty.items.length / 9) }, (_, i) => i + 1).map(num => (
                                                <button
                                                    key={num}
                                                    onClick={() => {
                                                        setFacultyPage(num);
                                                        window.scrollTo({ top: facultyGridRef.current?.offsetTop - 100, behavior: 'smooth' });
                                                    }}
                                                    style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: facultyPage === num ? '#0e2455' : '#ffffff', color: facultyPage === num ? '#ffffff' : '#475569', border: facultyPage === num ? '1px solid #0e2455' : '1px solid #e2e8f0', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>

                                        <button 
                                            onClick={() => {
                                                setFacultyPage(p => Math.min(Math.ceil(department.faculty.items.length / 9), p + 1));
                                                window.scrollTo({ top: facultyGridRef.current?.offsetTop - 100, behavior: 'smooth' });
                                            }}
                                            disabled={facultyPage === Math.ceil(department.faculty.items.length / 9)}
                                            className="faculty-pagination-btn"
                                        >
                                            Next Page
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 4. ACADEMICS TAB */}
                    {activeTab === 'academics' && department.academics && (
                        <div className="dept-tab-pane pane-academics">
                            {department.academics.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.academics.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW ACADEMICS DOC</a>
                                </div>
                            )}
                            
                            <AcademicsInteractiveExplorer academics={department.academics} />
                        </div>
                    )}

                    {/* 5. INDUSTRY INTERACTIONS TAB */}
                    {activeTab === 'industry' && department.industry && (
                        <div className="dept-tab-pane pane-industry">
                            {department.industry.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.industry.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW INDUSTRY RECORDS</a>
                                </div>
                            )}

                            {department.industry.guestLectures && <EventTable data={department.industry.guestLectures} expandable={true} />}
                            {department.industry.workshops && <EventTable data={department.industry.workshops} expandable={true} />}
                            {department.industry.industrialVisits && <EventTable data={department.industry.industrialVisits} expandable={true} />}
                            {department.industry.edActivities && <EventTable data={department.industry.edActivities} expandable={true} />}
                            {department.industry.webinars && <EventTable data={department.industry.webinars} expandable={true} />}
                            {department.industry.outboundTraining && <EventTable data={department.industry.outboundTraining} expandable={true} />}
                            {department.industry.alumniTalks && <EventTable data={department.industry.alumniTalks} expandable={true} />}
                            {department.industry.otherActivities && <EventTable data={department.industry.otherActivities} expandable={true} />}
                            {department.industry.interstateExchange && <EventTable data={department.industry.interstateExchange} expandable={true} />}

                            <CategorizedList categories={Object.fromEntries(Object.entries(department.industry).filter(([key]) => !['pdf', 'guestLectures', 'workshops', 'industrialVisits', 'events', 'edActivities', 'webinars', 'outboundTraining', 'alumniTalks', 'otherActivities', 'interstateExchange'].includes(key)))} />
                        </div>
                    )}
                    {/* 6. RESEARCH AND CONSULTANCY TAB (Lightwind UI Redesign) */}
                    {activeTab === 'research' && department.research && (
                        <div className="dept-tab-pane pane-research lw-research-root">
                            {department.research.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.research.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW RESEARCH DASHBOARD</a>
                                </div>
                            )}

                            {/* Center Details Hero / Banner */}
                            {department.research.centerDetails && (
                                <div className="lw-research-hero">
                                    <div className="lw-research-hero-bg"></div>
                                    <div className="lw-research-hero-content">
                                        <div className="lw-research-pill-badge" style={{ color: '#ffffff', fontWeight: 700 }}>
                                            <span className="lw-pill-indicator"></span>
                                            <span>VTU Recognized Center</span>
                                        </div>
                                        <h3 className="lw-research-hero-title" style={{ color: '#ffffff', fontWeight: 800 }}>Research Center &amp; Ph.D. Programs</h3>
                                        <p className="lw-research-hero-desc" style={{ color: '#f1f5f9' }}>{department.research.centerDetails}</p>
                                    </div>
                                </div>
                            )}

                            {/* Section Cards */}
                            <div className="lw-research-sections">
                                {[
                                    { key: 'publications', label: 'Research Publications', icon: 'file-text', tag: 'Journal & Papers' },
                                    { key: 'books', label: 'Book Publications', icon: 'book', tag: 'Authored Books & Chapters' },
                                    { key: 'researchPortfolio', label: 'Research Portfolio', icon: 'folder', tag: 'Grants & Projects' },
                                    { key: 'achievements', label: 'Patents & Faculty Achievements', icon: 'award', tag: 'Intellectual Property' },
                                    { key: 'studentPublications', label: 'Student Publications', icon: 'users', tag: 'Student Research' },
                                    { key: 'consultancy', label: 'Consultancy & Testing Services', icon: 'briefcase', tag: 'Industry Services' }
                                ].map(category => {
                                    const catData = department.research[category.key];
                                    if (!catData || !catData.items || catData.items.length === 0) return null;
                                    const validItems = catData.items.filter(item => item && (item.title || item.name));
                                    if (validItems.length === 0) return null;

                                    return (
                                        <div key={category.key} className="lw-research-card-block">
                                            <div className="lw-card-block-header">
                                                <div className="lw-card-block-header-left">
                                                    <span className="lw-card-block-tag">{category.tag}</span>
                                                    <h3 className="lw-card-block-title">{catData.title || category.label}</h3>
                                                </div>
                                                <span className="lw-card-block-count">{validItems.length} {validItems.length === 1 ? 'Record' : 'Records'}</span>
                                            </div>

                                            <div className="lw-research-grid">
                                                {validItems.map((item, idx) => {
                                                    const itemLink = item.pdf || item.link;
                                                    return (
                                                        <div key={idx} className="lw-research-item-card">
                                                            <div className="lw-item-card-top">
                                                                <div className="lw-item-idx-badge">{String(idx + 1).padStart(2, '0')}</div>
                                                                {item.date && <span className="lw-item-date">{item.date}</span>}
                                                            </div>
                                                            <p className="lw-item-title">{item.title || item.name}</p>
                                                            {item.resourcePerson && (
                                                                <p className="lw-item-author">{item.resourcePerson}</p>
                                                            )}
                                                            {itemLink ? (
                                                                <a 
                                                                    href={mediaUrl(itemLink)} 
                                                                    target="_blank" 
                                                                    rel="noreferrer" 
                                                                    className="lw-item-action-btn"
                                                                >
                                                                    <span>View Document</span>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                                                                </a>
                                                            ) : (
                                                                <div className="lw-item-published-chip">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                                    <span>Published Record</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Research Guides */}
                            {department.research.guides && department.research.guides.length > 0 && (
                                <div className="lw-research-card-block" style={{marginTop: '2rem'}}>
                                    <div className="lw-card-block-header">
                                        <div className="lw-card-block-header-left">
                                            <span className="lw-card-block-tag">Faculty Mentors</span>
                                            <h3 className="lw-card-block-title">Research Guides &amp; Scholars</h3>
                                        </div>
                                    </div>
                                    <div style={{overflowX: 'auto'}}>
                                        <table className="course-details-table" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                                            <thead>
                                                <tr style={{backgroundColor: '#f8fafc'}}>
                                                    <th style={{padding: '12px', textAlign: 'left', fontWeight: 700, color: '#0e2455'}}>Guide Name</th>
                                                    <th style={{padding: '12px', textAlign: 'center', fontWeight: 700, color: '#0e2455'}}>Sanctioned</th>
                                                    <th style={{padding: '12px', textAlign: 'center', fontWeight: 700, color: '#0e2455'}}>Vacancy</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {department.research.guides.map((guide, idx) => (
                                                    <tr key={idx} style={{borderBottom: '1px solid #f1f5f9'}}>
                                                        <td style={{padding: '12px', color: '#475569'}}>{guide.name}</td>
                                                        <td style={{padding: '12px', textAlign: 'center', color: '#475569'}}>{guide.sanctioned}</td>
                                                        <td style={{padding: '12px', textAlign: 'center', color: '#F6872A', fontWeight: 700}}>{guide.vacancy}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Doctorates Awarded */}
                            {department.research.doctoratesAwarded && department.research.doctoratesAwarded.length > 0 && (
                                <div className="lw-research-card-block" style={{marginTop: '2rem'}}>
                                    <div className="lw-card-block-header">
                                        <div className="lw-card-block-header-left">
                                            <span className="lw-card-block-tag">Alumni Ph.D.</span>
                                            <h3 className="lw-card-block-title">Doctorates Awarded</h3>
                                        </div>
                                    </div>
                                    <ul style={{listStyle: 'none', paddingLeft: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px', margin: 0}}>
                                        {department.research.doctoratesAwarded.map((name, idx) => (
                                            <li key={idx} style={{backgroundColor: '#f8fafc', padding: '15px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #f1f5f9'}}>
                                                <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#F6872A', flexShrink: 0}}></div>
                                                <span style={{fontWeight: 600, color: '#0e2455', fontSize: '0.85rem'}}>{name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <CategorizedList categories={{
                                "Conferences (Attended)": department.research.conferences?.attended,
                                "Conferences (Organised)": department.research.conferences?.organised,
                                "Seminars / Webinars (Attended)": department.research.seminars?.attended,
                                "Seminars / Webinars (Organised)": department.research.seminars?.organised,
                            }} />
                        </div>
                    )}

                    {/* 8. INFRASTRUCTURE TAB */}
                    {activeTab === 'infrastructure' && department.infrastructure && department.infrastructure.items && (
                        <InfrastructurePane data={department.infrastructure} />
                    )}

                    {/* 9. PLACEMENTS TAB */}
                    {activeTab === 'placements' && department.placements && (
                        <div className="dept-tab-pane pane-placements">
                            {department.placements.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.placements.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW PLACEMENT RECORDS</a>
                                </div>
                            )}

                            {department.placements.stats && (
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', marginBottom: '40px'}}>
                                    {department.placements.stats.map((stat, idx) => (
                                        <div key={idx} style={{background: 'linear-gradient(135deg, #0e2455 0%, #1a3a6c 100%)', padding: '30px', borderRadius: '20px', color: '#fff'}}>
                                            <p style={{fontSize: '3rem', fontWeight: 900, marginBottom: '10px'}}>{stat.value}</p>
                                            <p style={{fontSize: '1.1rem', fontWeight: 600, color: '#e0e7ff'}}>{stat.label}</p>
                                            {stat.description && <p style={{fontSize: '0.85rem', color: '#93c5fd', marginTop: '10px'}}>{stat.description}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {department.placements.trainings && <EventTable data={department.placements.trainings} />}

                            {Object.entries(department.placements)
                                .filter(([key]) => key !== 'stats' && key !== 'trainings' && key !== 'pdf')
                                .sort((a, b) => {
                                    const yearA = Math.max(extractYear(a[1]?.title), extractYear(a[0]));
                                    const yearB = Math.max(extractYear(b[1]?.title), extractYear(b[0]));
                                    if (yearA !== yearB) return yearB - yearA;
                                    return 0;
                                })
                                .map(([key, catData]) => {
                                const items = sortByLatestYear(catData?.items || []);
                                if (!catData || items.length === 0) return null;
                                return (
                                    <div key={key} className="reports-timeline-group">
                                        <h3 className="reports-timeline-group-title">{catData.title || key}</h3>
                                        <div className="reports-small-timeline">
                                            {items.map((item, idx) => (
                                                <div key={idx} className="reports-timeline-item">
                                                    <div className="reports-timeline-dot"></div>
                                                    <div className="reports-timeline-card">
                                                        <a href={mediaUrl(item.pdf || item.link) || '#'} target={item.pdf || item.link ? "_blank" : "_self"} rel="noreferrer" className="reports-timeline-link">
                                                            <span className="reports-timeline-title">{item.title}</span>
                                                            <span className="animated-icon-wrapper">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="reports-timeline-icon icon-default"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="reports-timeline-icon icon-download"><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path><polyline points="7 11 12 16 17 11"></polyline><line x1="12" y1="4" x2="12" y2="16"></line></svg>
                                                            </span>
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* 10. REPORTS TAB */}
                    {activeTab === 'reports' && department.reports && (
                        <div className="dept-tab-pane pane-reports">
                            {department.reports.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.reports.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW REPORTS ARCHIVE</a>
                                </div>
                            )}
                            <div className="reports-timeline-container">
                                {Object.entries(department.reports)
                                    .filter(([key]) => key !== 'pdf')
                                    .sort((a, b) => {
                                        const yearA = Math.max(extractYear(a[1]?.title), extractYear(a[0]));
                                        const yearB = Math.max(extractYear(b[1]?.title), extractYear(b[0]));
                                        if (yearA !== yearB) return yearB - yearA;
                                        return 0;
                                    })
                                    .map(([key, catData]) => {
                                    const items = sortByLatestYear(catData?.items || []);
                                    if (!catData || items.length === 0) return null;
                                    return (
                                        <div key={key} className="reports-timeline-group">
                                            <h3 className="reports-timeline-group-title">{catData.title || key}</h3>
                                            <div className="reports-small-timeline">
                                                {items.map((item, idx) => (
                                                    <div key={idx} className="reports-timeline-item">
                                                        <div className="reports-timeline-dot"></div>
                                                        <div className="reports-timeline-card">
                                                            {item.pdf || item.link ? (
                                                                <a href={mediaUrl(item.pdf || item.link)} target="_blank" rel="noreferrer" className="reports-timeline-link">
                                                                    <span className="reports-timeline-title">{item.title}</span>
                                                                    <span className="animated-icon-wrapper">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="reports-timeline-icon icon-default"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="reports-timeline-icon icon-download"><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path><polyline points="7 11 12 16 17 11"></polyline><line x1="12" y1="4" x2="12" y2="16"></line></svg>
                                                                    </span>
                                                                </a>
                                                            ) : (
                                                                <div className="reports-timeline-link" style={{ cursor: 'default' }}>
                                                                    <span className="reports-timeline-title">{item.title}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 11. ALUMNI TAB */}
                    {activeTab === 'alumni' && department.alumni && (
                        <div className="dept-tab-pane pane-alumni">
                            {department.alumni.pdf && (
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
                                    <a href={mediaUrl(department.alumni.pdf)} target="_blank" rel="noreferrer" className="view-all-btn">VIEW ALUMNI DOCUMENTS</a>
                                </div>
                            )}

                            {department.alumni.talks && <EventTable data={department.alumni.talks} />}
                            
                            {department.alumni.meet && (
                                <div className="alumni-meet-section" style={{marginBottom: '3rem'}}>
                                    <h3 style={{fontSize: '1.75rem', fontWeight: 700, color: '#0e2455', marginBottom: '1.5rem'}}>{department.alumni.meet.title}</h3>
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px'}}>
                                        {department.alumni.meet.items.map((item, idx) => (
                                            <div key={idx} style={{backgroundColor: '#f8fafc', borderRadius: '16px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', border: '1px solid #f1f5f9'}}>
                                                <h4 style={{fontSize: '1.1rem', fontWeight: 700, color: '#0e2455', margin: 0}}>{item.title}</h4>
                                                {item.description && <p style={{fontSize: '0.9rem', color: '#64748b', margin: 0}}>{item.description}</p>}
                                                {item.date && <p style={{fontSize: '0.85rem', color: '#F6872A', margin: 0, fontWeight: 500}}>{item.date}</p>}
                                                {item.pdf && (
                                                    <a href={mediaUrl(item.pdf)} target="_blank" rel="noreferrer" style={{marginTop: '1rem', color: '#F6872A', fontWeight: 600, textDecoration: 'none', fontSize: '0.9rem'}}>View Report →</a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {department.alumni.testimonials && department.alumni.testimonials.length > 0 && (
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '30px', marginTop: '40px'}}>
                                    {department.alumni.testimonials.map((t, idx) => (
                                        <div key={idx} style={{backgroundColor: '#003333', color: '#fff', padding: '30px', borderRadius: '20px', position: 'relative'}}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{position: 'absolute', bottom: '10px', right: '10px'}}><path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12"></path><path d="M11 8l-3 3l3 3"></path><path d="M16 11h-8"></path></svg>
                                            <p style={{fontStyle: 'italic', marginBottom: '20px', lineHeight: 1.6, color: 'rgba(255,255,255,0.8)', position: 'relative', zIndex: 1}}>"{t.content}"</p>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px', position: 'relative', zIndex: 1}}>
                                                {t.image && <img src={mediaUrl(t.image)} alt={t.name} style={{width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)'}} />}
                                                <div>
                                                    <h4 style={{margin: 0, fontSize: '1.1rem'}}>{t.name}</h4>
                                                    <p style={{margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)'}}>Batch {t.batch}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
                
                {/* EXPLORE OTHER DEPARTMENTS SECTION */}
                <div className="dept-explore-other-section">
                    <div className="explore-bg-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                    </div>
                    <div className="explore-container">
                        <div className="explore-header-row">
                            <div className="explore-title-block">
                                <span className="explore-subtitle">ACADEMIC EXCELLENCE</span>
                                <h2 className="explore-title">
                                    Explore Other <br />
                                    <span className="text-orange">Departments</span>
                                </h2>
                            </div>
                            <div className="explore-btn-block">
                                <Link to="/departments" className="view-all-btn">VIEW ALL DEPARTMENTS</Link>
                            </div>
                        </div>

                        <div className="explore-cards-row">
                            <Link to="/department/computer-science-and-engineering" className="explore-card-item">
                                <span className="card-category">POPULAR</span>
                                <h4 className="card-dept-name">Computer Science & Engineering</h4>
                            </Link>
                            <Link to="/department/electronics-and-communication-engineering" className="explore-card-item">
                                <span className="card-category">ENGINEERING</span>
                                <h4 className="card-dept-name">Electronics & Communication</h4>
                            </Link>
                            <Link to="/department/department-of-mba" className="explore-card-item">
                                <span className="card-category">MANAGEMENT</span>
                                <h4 className="card-dept-name">Master of Business Administration</h4>
                            </Link>
                        </div>
                    </div>
                </div>

            </div>
            <Footer />
        </>
    );
};

export default DepartmentDetails;
